/**
 * Extends Quill's built-in Image format so `class` (float) and `style`
 * (width, set by the image toolbar) survive HTML -> Delta -> HTML round-trips
 * (quill.clipboard.convert(), used whenever existing content is loaded back
 * into the editor). Quill's default Image blot only recognizes alt/height/width,
 * so without this, resized/floated images revert to their original size the
 * next time the edit form is reopened.
 */

interface QuillBlotStatic {
    formats(domNode: HTMLElement): Record<string, string>;
}

interface QuillEmbedInstance {
    domNode: HTMLElement;
    format(name: string, value: unknown): void;
}

type QuillImageBlotClass = (new (...args: never[]) => QuillEmbedInstance) & QuillBlotStatic;

interface QuillLike {
    import(name: string): unknown;
    register(formats: Record<string, unknown>, overwrite?: boolean): void;
}

const EXTRA_ATTRIBUTES = ["class", "style"];

let registered = false;

class WysiwygImageFormatUtils {
    public static register(Quill: QuillLike | null | undefined): void {
        if (registered || !Quill) return;

        const BaseImage = Quill.import("formats/image") as QuillImageBlotClass | null;
        if (!BaseImage) return;

        class ExtendedImage extends BaseImage {
            public static override formats(domNode: HTMLElement): Record<string, string> {
                const formats = BaseImage.formats(domNode);
                EXTRA_ATTRIBUTES.forEach((attribute) => {
                    if (domNode.hasAttribute(attribute)) {
                        formats[attribute] = domNode.getAttribute(attribute) || "";
                    }
                });
                return formats;
            }

            public override format(name: string, value: unknown): void {
                if (EXTRA_ATTRIBUTES.includes(name)) {
                    if (value) {
                        this.domNode.setAttribute(name, String(value));
                    } else {
                        this.domNode.removeAttribute(name);
                    }
                    return;
                }
                super.format(name, value);
            }
        }

        Quill.register({ "formats/image": ExtendedImage }, true);
        registered = true;
    }
}

export { WysiwygImageFormatUtils };
